import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { LANGUAGES } from "@/lib/i18n";
import {
  LogOut, ChevronDown, HardHat, Menu, X, Briefcase, Info,
  LogIn, UserPlus, LayoutDashboard,
  Home, Users, Handshake, LayoutGrid, Crown, BookOpen, Mail,
} from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Prevent body scroll while drawer open (mobile)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  const handleLogout = () => { logout(); setOpen(false); nav("/"); };

  const LangSelect = ({ testid = "lang-select" }) => (
    <div className="relative">
      <select
        data-testid={testid}
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        className="appearance-none rounded-xl border-2 border-sarthi-border bg-white py-2 pl-3 pr-8 text-sm font-bold text-sarthi-text focus:border-sarthi-primary focus:outline-none"
      >
        {LANGUAGES.map((l) => (<option key={l.code} value={l.code}>{l.native}</option>))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-sarthi-muted" />
    </div>
  );

  return (
    <>
      <header className="sticky top-0 z-40 border-b-2 border-sarthi-border bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 lg:gap-4 lg:px-6">
        <Link to="/" data-testid="brand-link" className="flex flex-shrink-0 items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white sm:h-11 sm:w-11">
            <img
              src="/sarthi-logo-icon.png"
              alt="Sarthi Associates logo"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="flex flex-col justify-center leading-none">
            <div className="whitespace-nowrap font-display text-lg font-extrabold text-sarthi-text sm:text-xl">{t("brand")}</div>
            <div className="mt-0.5 hidden whitespace-nowrap text-[11px] font-semibold text-sarthi-muted sm:block">{t("tagline")}</div>
          </div>
        </Link>

        {/* Desktop nav (>=lg) — professional icon + navy/slate palette with animated underline */}
        <nav className="hidden flex-1 items-center justify-end gap-0.5 lg:flex xl:gap-1" data-testid="desktop-nav">
          <NavItem to="/"                icon={Home}       label="Home"             loc={loc} testid="header-home-link" />
          <NavItem to="/about"           icon={Info}       label={t("about")}       loc={loc} testid="header-about-link" />
          <NavItem to="/executive-teams" icon={Users}      label="Our Team"         loc={loc} testid="header-our-team-link" />
          <NavItem to="/partners"        icon={Handshake}  label="Business Partner" loc={loc} testid="header-partners-link" />
          <NavItem to="/consultancy"     icon={Briefcase}  label="Services"         loc={loc} testid="header-services-link" />
          <NavItem to="/jobs"            icon={LayoutGrid} label="Categories"       loc={loc} testid="header-categories-link" />
          <NavItem to="/plans"           icon={Crown}      label="Premium Plans"    loc={loc} testid="header-plans-link" gold />
          <NavItem to="/blog"            icon={BookOpen}   label="Blog"             loc={loc} testid="header-blog-link" />
          <a
            href="mailto:info@sarthiassociates.com"
            data-testid="header-contact-email"
            className="ml-1 hidden items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold tracking-tight text-slate-500 transition-colors hover:text-sarthi-secondary xl:inline-flex"
            title="Email us — info@sarthiassociates.com"
          >
            <Mail className="h-4 w-4" strokeWidth={2.25} />
            <span className="hidden xl:inline">info@sarthiassociates.com</span>
          </a>
          {(user?.role === "contractor" || user?.role === "hr") && (
            <NavItem to="/hr"                  icon={LayoutDashboard} label="HR"       loc={loc} testid="header-hr-link" />
          )}
          {(user?.role === "contractor" || user?.role === "hr") && (
            <NavItem to="/contractor/pipeline" icon={Briefcase}       label="Pipeline" loc={loc} testid="header-pipeline-link" />
          )}
          <LangSelect />
          {user ? (
            <>
              {loc.pathname !== "/dashboard" && (
                <Link to="/dashboard" data-testid="header-dashboard-link" className="sarthi-btn-outline px-4 py-2">
                  {t("dashboard")}
                </Link>
              )}
              <button data-testid="logout-btn" onClick={handleLogout} className="sarthi-btn-outline inline-flex items-center gap-2 px-4 py-2">
                <LogOut className="h-4 w-4" /> {t("logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-link" className="sarthi-btn-outline px-4 py-2">{t("login")}</Link>
              <Link to="/register?role=worker" data-testid="header-register-link" className="sarthi-btn-primary px-4 py-2 text-white">{t("register")}</Link>
            </>
          )}
        </nav>

        {/* Mobile: persistent Categories pill + hamburger */}
        <div className="flex flex-shrink-0 items-center gap-1.5 lg:hidden">
          <Link
            to="/jobs"
            data-testid="header-categories-link-mobile"
            className="inline-flex h-10 flex-shrink-0 items-center gap-1 rounded-xl border-2 border-sarthi-border bg-white px-2.5 text-[11px] font-bold uppercase tracking-wide text-sarthi-text hover:border-sarthi-primary hover:text-sarthi-primary"
          >
            <Briefcase className="h-4 w-4" strokeWidth={2.5} />
            {t("nav_categories")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border-2 border-sarthi-border bg-white text-sarthi-text hover:border-sarthi-primary"
          >
            {open ? <X className="h-5 w-5" strokeWidth={2.5} /> : <Menu className="h-5 w-5" strokeWidth={2.5} />}
          </button>
        </div>
      </div>
      </header>

      {/* Mobile drawer — rendered OUTSIDE <header> so that <header>'s backdrop-blur
          does not become the containing block for these fixed-position children. */}
      {open && (
        <>
          <div
            className="fixed inset-0 top-[64px] z-30 bg-black/40 lg:hidden"
            onClick={() => setOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <nav
            data-testid="mobile-menu"
            className="fixed inset-x-0 top-[64px] z-40 border-b-2 border-sarthi-border bg-white p-5 shadow-[0_8px_0_0_rgba(0,0,0,0.06)] lg:hidden"
          >
            <div className="space-y-2">
              <MobileLink to="/"                icon={Home}            label="Home"                 testid="mobile-home-link" />
              <MobileLink to="/jobs"             icon={LayoutGrid}      label={t("nav_categories")}  testid="mobile-categories-link" />
              <MobileLink to="/about"            icon={Info}            label={t("about")}           testid="mobile-about-link" />
              <MobileLink to="/executive-teams"  icon={Users}           label="Our Team"             testid="mobile-our-team-link" />
              <MobileLink to="/partners"         icon={Handshake}       label="Business Partner"     testid="mobile-partners-link" />
              <MobileLink to="/consultancy"      icon={Briefcase}       label="Services"             testid="mobile-services-link" />
              <MobileLink to="/plans"            icon={Crown}           label="Premium Plans"        testid="mobile-plans-link" />
              <MobileLink to="/blog"             icon={BookOpen}        label="Blog"                 testid="mobile-blog-link" />
              {user && loc.pathname !== "/dashboard" && (
                <MobileLink to="/dashboard" icon={LayoutDashboard} label={t("dashboard")} testid="mobile-dashboard-link" />
              )}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl border-2 border-sarthi-border bg-sarthi-accent/40 p-3">
              <div className="text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t("language")}</div>
              <LangSelect testid="mobile-lang-select" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              {user ? (
                <button
                  data-testid="mobile-logout-btn"
                  onClick={handleLogout}
                  className="sarthi-btn-outline col-span-2 inline-flex items-center justify-center gap-2 py-3 text-base font-bold"
                >
                  <LogOut className="h-5 w-5" /> {t("logout")}
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="mobile-login-link"
                    className="sarthi-btn-outline inline-flex items-center justify-center gap-2 py-3 text-base font-bold"
                  >
                    <LogIn className="h-5 w-5" /> {t("login")}
                  </Link>
                  <Link
                    to="/register"
                    data-testid="mobile-register-link"
                    className="sarthi-btn-primary inline-flex items-center justify-center gap-2 py-3 text-base font-bold text-white"
                  >
                    <UserPlus className="h-5 w-5" /> {t("register")}
                  </Link>
                </>
              )}
            </div>
          </nav>
        </>
      )}
    </>
  );
}

function NavItem({ to, icon: Icon, label, loc, testid, gold }) {
  const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
  // Palette: navy = #1F3A64 (sarthi-secondary), slate = slate-500, gold = amber for Premium
  const baseText = gold
    ? "text-amber-600 hover:text-amber-500"
    : active
    ? "text-sarthi-secondary"
    : "text-slate-500 hover:text-sarthi-secondary";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group relative inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-semibold tracking-tight transition-colors duration-200 ${baseText}`}
    >
      <Icon
        className={`h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 ${gold ? "text-amber-500" : ""}`}
        strokeWidth={2.25}
      />
      <span>{label}</span>
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-x-2 -bottom-0.5 h-[2px] origin-left rounded-full transition-transform duration-300 ${gold ? "bg-amber-400" : "bg-sarthi-secondary"} ${active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
      />
    </Link>
  );
}


function MobileLink({ to, icon: Icon, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-3 rounded-xl border-2 border-sarthi-border bg-white px-4 py-3 text-base font-bold text-sarthi-text hover:border-sarthi-primary hover:bg-sarthi-accent/50"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sarthi-accent text-sarthi-primary">
        <Icon className="h-5 w-5" strokeWidth={2.5} />
      </span>
      {label}
    </Link>
  );
}
